import { isIosNative } from '@/renderer/utils/isIosNative';

/**
 * The on-screen keyboard on the native iOS shell: ONE app-wide listener and
 * ONE published value, `--kb-inset` on <html> (#2142).
 *
 * ## Why the app tracks the keyboard itself
 *
 * The shell runs `Keyboard.resize: 'none'` (capacitor.config.ts): the WKWebView
 * stays full-screen and the page makes room for the keyboard on its own. With
 * the old `'native'` mode the plugin shrank the WebView ~0.45s AFTER the
 * keyboard finished rising (it schedules the resize for the animation duration
 * plus 0.2s), so every screen sat still while the keyboard covered it and then
 * jumped. Only support chat had been taught to move early; the sign-in form
 * trailed the keyboard and the release-center composer showed the tab bar and
 * page content around the keyboard until the late resize landed.
 *
 * `keyboardWillShow` fires before the animation starts and carries the final
 * height, so the page can move in step with it:
 *
 *  - `--kb-inset` is the keyboard's height while it is up and 0 while it is
 *    down. It is a registered `<length>` that <html> transitions on the iOS
 *    keyboard's own timing (`--kb-dur` / `--kb-ease`, picked by `data-kb`), so
 *    every rule that reads it moves on the same frame and the same curve. See
 *    "Keyboard inset" in renderer/styles/index.css for the derived values
 *    (`--bottom-inset`, `--kb-lift`) and who reads them.
 *  - `data-kb="open" | "closed"` selects that curve. CSS takes the timing of
 *    the state being entered, so it is written in the same call as the inset.
 *  - `data-kb-sync` marks that the inset is live, so surfaces that used to
 *    ease their own height (support chat, the full-screen sheets) can stop: a
 *    second transition chasing an already-animated value lags behind it.
 *
 * The keyboard height iOS reports includes the home-indicator inset (the
 * keyboard covers it), so nothing reading `--kb-inset` adds the bottom safe
 * area on top of it.
 *
 * Focused fields are kept above the keyboard by revealFocusedField below; with
 * the WebView no longer resized, nothing else does that.
 *
 * Inert off the iOS shell: web, the PWA and Android never start the tracker,
 * so `--kb-inset` stays at its registered 0px and every derived value reduces
 * to what it was before.
 */

export type KeyboardState = {
  /** The keyboard is up (between keyboardWillShow and keyboardWillHide). */
  open: boolean;
  /** Its height in CSS px, home indicator included. 0 while it is down. */
  height: number;
};

const CLOSED: KeyboardState = { open: false, height: 0 };

let state: KeyboardState = CLOSED;
const subscribers = new Set<() => void>();
let refs = 0;
let teardown: (() => void) | null = null;

const noop = () => undefined;

export function getKeyboardState(): KeyboardState {
  return state;
}

function publish(next: KeyboardState) {
  if (next.open === state.open && next.height === state.height) {
    return;
  }

  state = next;

  const root = document.documentElement;

  // Curve first, then the value: the transition that runs is the one in force
  // when the value changes.
  root.dataset.kb = next.open ? 'open' : 'closed';
  root.style.setProperty('--kb-inset', `${next.height}px`);

  for (const notify of subscribers) {
    notify();
  }
}

/**
 * Start listening (refcounted; the first caller installs the listeners, the
 * last release removes them and clears what they wrote). Returns the release.
 * A no-op off the native iOS shell and during SSR.
 */
export function retainKeyboardTracking(): () => void {
  if (typeof window === 'undefined' || !isIosNative()) {
    return noop;
  }

  refs += 1;

  if (refs === 1) {
    teardown = install();
  }

  let released = false;

  return () => {
    if (released) {
      return;
    }

    released = true;
    refs -= 1;

    if (refs === 0) {
      teardown?.();
      teardown = null;
    }
  };
}

/** For useSyncExternalStore. Subscribing also keeps the tracker running. */
export function subscribeKeyboard(notify: () => void): () => void {
  subscribers.add(notify);
  const release = retainKeyboardTracking();

  return () => {
    subscribers.delete(notify);
    release();
  };
}

function install(): () => void {
  const root = document.documentElement;
  let cancelled = false;
  let removeListeners: (() => void) | undefined;
  let stopReveal: (() => void) | undefined;

  root.dataset.kbSync = '';

  const reveal = () => {
    stopReveal?.();
    stopReveal = revealFocusedField(() => state);
  };

  // A field focused while the keyboard is already up (next field, a tap on a
  // different input) gets no keyboardWillShow, so reveal it from here.
  const onFocusIn = () => {
    if (state.open) {
      reveal();
    }
  };

  // A field that grows while the keyboard is up (a textarea gaining a line)
  // can push its bottom edge under the keyboard; follow it.
  const onInput = (event: Event) => {
    if (state.open && event.target === document.activeElement) {
      reveal();
    }
  };

  document.addEventListener('focusin', onFocusIn);
  document.addEventListener('input', onInput);

  // Ground truth from the shell (AppDelegate.swift): the keyboard's height as
  // UIKit last reported it, sent when the app becomes active again. Going to
  // the background with the keyboard up hides and re-raises it, and a will-
  // event that lands while the WebView is suspended can be lost, which used to
  // leave the page lifted over no keyboard. This settles it either way.
  const onNativeSync = (event: Event) => {
    const height = Math.round(Number((event as CustomEvent<{ height?: number }>).detail?.height));

    if (!Number.isFinite(height)) {
      return;
    }

    if (height > 0) {
      publish({ open: true, height });
      // Back from the background the keyboard is up again, and the field it
      // belongs to may have lost its place; reveal it like any other open.
      reveal();
    } else {
      stopReveal?.();
      releaseScrollRoom();
      publish(CLOSED);
    }
  };

  window.addEventListener('ras:keyboard-sync', onNativeSync);

  void import('@capacitor/keyboard')
    .then(({ Keyboard }) => {
      const willShow = Keyboard.addListener('keyboardWillShow', (info) => {
        publish({ open: true, height: Math.max(0, Math.round(info.keyboardHeight)) });
        reveal();
      });
      const willHide = Keyboard.addListener('keyboardWillHide', () => {
        stopReveal?.();
        releaseScrollRoom();
        publish(CLOSED);
      });
      // The did- events confirm the will- ones once the keyboard settles. Both
      // are no-ops when they agree; they only correct a will- event that never
      // arrived or a height that changed in between.
      const didShow = Keyboard.addListener('keyboardDidShow', (info) => {
        publish({ open: true, height: Math.max(0, Math.round(info.keyboardHeight)) });
      });
      const didHide = Keyboard.addListener('keyboardDidHide', () => {
        releaseScrollRoom();
        publish(CLOSED);
      });

      removeListeners = () => {
        for (const listener of [willShow, willHide, didShow, didHide]) {
          void listener.then((handle) => handle.remove());
        }
      };

      // Released while the import was in flight (a fast unmount on a cold
      // load): tear straight down, or the listeners outlive every consumer and
      // keep writing <html>.
      if (cancelled) {
        removeListeners();
      }
    })
    .catch(() => {
      // Plugin unavailable: the inset stays 0, i.e. the layout simply doesn't
      // make room for the keyboard. Never break the page over it.
    });

  return () => {
    cancelled = true;
    removeListeners?.();
    stopReveal?.();
    releaseScrollRoom();
    document.removeEventListener('focusin', onFocusIn);
    document.removeEventListener('input', onInput);
    window.removeEventListener('ras:keyboard-sync', onNativeSync);
    state = CLOSED;
    root.style.removeProperty('--kb-inset');
    delete root.dataset.kb;
    delete root.dataset.kbSync;

    for (const notify of subscribers) {
      notify();
    }
  };
}

// ---------------------------------------------------------------------------
// Keeping the focused field above the keyboard
// ---------------------------------------------------------------------------

/** Space left between a revealed field and the keyboard's top edge. */
const REVEAL_MARGIN = 12;
/** How long to keep tracking: the open curve (0.3s) plus a few frames. */
const REVEAL_MS = 450;

function isTextEntry(el: Element | null): el is HTMLElement {
  if (!(el instanceof HTMLElement)) {
    return false;
  }

  if (el.isContentEditable || el instanceof HTMLTextAreaElement) {
    return true;
  }

  if (el instanceof HTMLSelectElement) {
    return true;
  }

  return (
    el instanceof HTMLInputElement &&
    ![
      'button',
      'checkbox',
      'color',
      'file',
      'hidden',
      'image',
      'radio',
      'range',
      'reset',
      'submit',
    ].includes(el.type)
  );
}

/**
 * The nearest ancestor that actually scrolls vertically. An `overflow: hidden`
 * box is skipped on purpose: script can still scroll it, and that is exactly
 * how WKWebView's own reveal used to throw the support-chat composer under the
 * status bar (#1579). Inside a `position: fixed` layer (a dialog, a sheet) the
 * search stops at that layer: scrolling the document would not move it. The
 * document's scroller counts only when the page lets it scroll.
 *
 * A box that could scroll but has nothing to scroll yet still counts: the
 * reveal gives it room (see ensureScrollRoom).
 */
export function scrollParentOf(el: HTMLElement): HTMLElement | null {
  for (let node = el.parentElement; node; node = node.parentElement) {
    if (node === document.body || node === document.documentElement) {
      break;
    }

    const { overflowY, position } = getComputedStyle(node);

    if (overflowY === 'auto' || overflowY === 'scroll') {
      return node;
    }

    if (position === 'fixed') {
      return null;
    }
  }

  const doc = document.scrollingElement as HTMLElement | null;

  if (!doc) {
    return null;
  }

  const hidden = (n: Element) => getComputedStyle(n).overflowY === 'hidden';

  return hidden(document.documentElement) || hidden(document.body) ? null : doc;
}

/**
 * Inner scroll containers that were given extra bottom padding for the
 * keyboard, with the inline value to restore. Only <main> reserves the
 * keyboard's height by itself; a box sized to the viewport, or one whose end
 * sits under the keyboard, otherwise has no room to scroll its last field up.
 */
const paddedScrollers = new Map<HTMLElement, { inline: string; computed: string; room: number }>();

/** Let `scroller` scroll its content up to `keyboardTop`. */
function ensureScrollRoom(scroller: HTMLElement, keyboardTop: number) {
  if (scroller === document.scrollingElement) {
    return;
  }

  const hidden = Math.ceil(scroller.getBoundingClientRect().bottom - keyboardTop);
  let padded = paddedScrollers.get(scroller);

  if (hidden <= (padded?.room ?? 0)) {
    return;
  }

  if (!padded) {
    padded = {
      inline: scroller.style.paddingBottom,
      computed: getComputedStyle(scroller).paddingBottom || '0px',
      room: 0,
    };
    paddedScrollers.set(scroller, padded);
  }

  padded.room = hidden;
  scroller.style.paddingBottom = `calc(${padded.computed} + ${hidden}px)`;
}

/** Undo every ensureScrollRoom, once the keyboard is going away. */
export function releaseScrollRoom() {
  for (const [scroller, { inline }] of paddedScrollers) {
    scroller.style.paddingBottom = inline;
  }

  paddedScrollers.clear();
}

function currentInset(): number {
  return (
    Number.parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--kb-inset')) ||
    0
  );
}

/**
 * Follow the keyboard up for one open: each frame, if the focused field's
 * bottom is below the keyboard's CURRENT top edge (the eased `--kb-inset`, not
 * the final height), or below the bottom of its own scroll container, scroll
 * that container by exactly the overlap. The field
 * therefore rides up with the keyboard instead of being covered and then
 * scrolled into view afterwards. Fields that already clear the keyboard (the
 * docked composers, which ride `--kb-inset` themselves) are never touched.
 * Returns a stop function.
 */
export function revealFocusedField(read: () => KeyboardState): () => void {
  let frame = 0;
  const until = performance.now() + REVEAL_MS;

  const step = () => {
    frame = 0;

    const { open, height } = read();
    const el = document.activeElement;

    if (!open || !isTextEntry(el)) {
      return;
    }

    // Track the eased inset while it is still moving, but never ask for less
    // room than the keyboard will finally take once the motion is over.
    const done = performance.now() >= until;
    const inset = done ? height : Math.min(height, currentInset());
    const keyboardTop = window.innerHeight - inset;
    const rect = el.getBoundingClientRect();
    const scroller = scrollParentOf(el);
    const isDocument = !scroller || scroller === document.scrollingElement;
    const box = isDocument ? null : scroller.getBoundingClientRect();
    // The field has to clear the keyboard AND stay inside its own scroller's
    // visible box: a dialog that shrinks with the keyboard otherwise clips the
    // field behind its own footer, above the keyboard but out of sight.
    const limit = box ? Math.min(keyboardTop, box.bottom) : keyboardTop;
    const overlap = rect.bottom - (limit - REVEAL_MARGIN);

    if (scroller && overlap > 0.5 && rect.bottom > limit - REVEAL_MARGIN / 2) {
      ensureScrollRoom(scroller, keyboardTop);

      // Never push the field's top above the scroller's own top edge: a field
      // taller than the room left (a long textarea) keeps its start, where the
      // caret usually is.
      const top = box ? box.top : 0;
      const delta = Math.min(overlap, Math.max(0, rect.top - top - REVEAL_MARGIN));

      if (delta > 0) {
        scroller.scrollTop += delta;
      }
    }

    if (!done) {
      frame = window.requestAnimationFrame(step);
    }
  };

  frame = window.requestAnimationFrame(step);

  return () => {
    if (frame) {
      window.cancelAnimationFrame(frame);
      frame = 0;
    }
  };
}
